import { useContext, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomTooltip from "../CustomTooltip";
import KycDetailsModal from "../KycDetailsModal";
import KycFormDoneDialog from "../KycFormDoneDialog";
import { AppContext } from "../../Context/AppContext";
import { apiPostRequest } from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";
import {
  KYC_STATUS,
  hasKycNotice,
  needsKycDetails,
} from "../../Constants/kycStatus";
// ⚠️ TEMP DEV-PHASE PREVIEW — remove with the KYC preview seed.
import { readKycPreviewFlag, setPreviewKycStatus } from "../../Constants/kycPreview";

// The one place the supplier's KYC state is written down. It sits in the
// sidebar under the business name, and again beside every control it blocks,
// so a disabled button always carries its own reason.
//
// In the first state the supplier has something to DO — fill in Grow's form —
// so there the chip is a button that opens it. In the others it only explains.
// Nothing is shown once the supplier is approved: they were told by
// notification, and a permanent "approved" badge is noise from then on.

const TONE = {
  [KYC_STATUS.DETAILS_REQUIRED]: {
    color: "rgba(37, 124, 109, 1)",
    background: "rgba(37, 124, 109, 0.12)",
  },
  [KYC_STATUS.PENDING]: {
    color: "rgba(223, 131, 80, 1)",
    background: "rgba(235, 150, 96, 0.15)",
  },
  [KYC_STATUS.REJECTED]: {
    color: "rgba(196, 60, 60, 1)",
    background: "rgba(196, 60, 60, 0.12)",
  },
};

const chipBase = {
  height: "18px",
  padding: "0 8px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: 500,
  cursor: "default",
};

const COPY_KEY = {
  [KYC_STATUS.DETAILS_REQUIRED]: "details",
  [KYC_STATUS.PENDING]: "pending",
  [KYC_STATUS.REJECTED]: "rejected",
};

/**
 * With no children: the chip itself, with its explanation on hover.
 * With children (a disabled control): only the explanation, wrapped around
 * them — used where a row has no room for a chip without widening the table.
 */
const KycStatusChip = ({ sx, children }) => {
  const { kycStatus, isCommunityManagementRole, refreshKycStatus, setKycStatus } =
    useContext(AppContext);
  const { t } = useTranslation("supplier-kyc");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [askingDone, setAskingDone] = useState(false);

  // Supplier-only: the same person managing a community is not held up by it.
  if (isCommunityManagementRole || !hasKycNotice(kycStatus)) return children ?? null;

  const copyKey = COPY_KEY[kycStatus];
  const tone = TONE[kycStatus];
  // Only the first state is actionable — everywhere else there is nothing the
  // supplier can press, so the chip stays a label.
  const actionable = needsKycDetails(kycStatus) && !children;

  // The form tells us nothing when it closes — sent, or abandoned halfway — so
  // closing it only asks the supplier which it was.
  const closeDetails = () => {
    setDetailsOpen(false);
    setAskingDone(true);
  };

  // "Done" is what moves the supplier to waiting for approval.
  const markDone = async () => {
    setAskingDone(false);
    // ⚠️ TEMP DEV-PHASE PREVIEW — there is no server to tell, so the switch
    // moves the preview along instead.
    if (readKycPreviewFlag() && setKycStatus) {
      setPreviewKycStatus(KYC_STATUS.PENDING);
      setKycStatus(KYC_STATUS.PENDING);
      return;
    }
    try {
      await apiPostRequest(endpoints.kycFormSubmitted);
    } catch {
      // The chip catches up on the next load; nothing to tell the supplier.
    }
    refreshKycStatus?.();
  };

  // "Later" changes nothing: the chip still asks for the details, and still
  // opens the form.
  const finishLater = () => setAskingDone(false);

  return (
    <>
      <CustomTooltip
        title={t(`${copyKey}.tooltip`)}
        tooltipSxStyles={{ maxWidth: "260px", padding: "8px 10px" }}
      >
        {children ? (
          // A disabled button swallows hover events, so the tooltip listens on a
          // wrapper around it.
          <Box component="span" sx={{ display: "inline-flex", width: "100%" }}>
            {children}
          </Box>
        ) : (
          <Box
            component={actionable ? "button" : "span"}
            type={actionable ? "button" : undefined}
            onClick={actionable ? () => setDetailsOpen(true) : undefined}
            sx={{
              ...chipBase,
              ...tone,
              ...(actionable
                ? {
                    border: "none",
                    cursor: "pointer",
                    textDecoration: "underline",
                    textUnderlineOffset: "2px",
                    font: "inherit",
                    fontSize: "11px",
                    fontWeight: 500,
                  }
                : {}),
              ...sx,
            }}
          >
            {t(`${copyKey}.chip`)}
          </Box>
        )}
      </CustomTooltip>

      {actionable && (
        <KycDetailsModal open={detailsOpen} onClose={closeDetails} />
      )}
      <KycFormDoneDialog open={askingDone} onDone={markDone} onLater={finishLater} />
    </>
  );
};

export default KycStatusChip;
